// The MIS journey (HM2, owner L2), one file in a full-width drawer over the MIS inbox, one step per screen with Back / Next:
// 1 choose (the file, its preview, its lender, its MIS date: the day it arrived, or earlier for a backdated MIS) → 2 map
// (columns, the extra columns kept as metadata, date order, status words; a new word stops here until it has a stage; saved
// for the lender) → 3 match (how each row was found; not-matched rows) → 4 check (the stage changes,
// nothing saved yet) → 5 apply. Only the preview shows values (mobiles, PAN and e-mails partly hidden, audit-logged);
// every other step shows column headers, lead codes and references, never a name or a mobile.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CalendarClock, Check, CircleHelp, Clock, Eraser, FileSpreadsheet, IndianRupee, Info, Link2, Mail, Minus, NotebookPen, RefreshCw, Rows3, Upload, type LucideIcon } from 'lucide-react'
import { Badge, ConfirmDialog, Drawer, StatCard, formatDateTime, notify } from '@/kit/ui'
import { FIELDS, MATCHED_BY, NEVER_KEPT, STAGES, download, fileState, indiaDay, mis, notesCsv, stageLabel, type DateOrder, type MisCheck, type MisField, type MisFile, type MisFileView, type MisMapping, type MisPreview, type MisRowNote, type MisSettings, type MisStage } from '@/lib/misApi'

const STEPS = ['Choose', 'Map', 'Match', 'Check', 'Apply'] as const
type Step = 1 | 2 | 3 | 4 | 5
const rupees = (n: number | null) => (n === null ? '—' : `₹${n.toLocaleString('en-IN')}`)

/** onChanged: the file was applied or removed (the inbox reloads). */
export function MisJourney({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [view, setView] = useState<MisFileView | null>(null)
  const [settings, setSettings] = useState<MisSettings | null>(null)
  const [preview, setPreview] = useState<MisPreview | null>(null)
  const [step, setStep] = useState<Step>(1)
  const [lender, setLender] = useState('')
  const [columns, setColumns] = useState<Partial<Record<MisField, string>>>({})
  const [dateOrder, setDateOrder] = useState<DateOrder>('dmy')
  const [keep, setKeep] = useState<string[]>([])
  const [misDay, setMisDay] = useState('')
  const [mapping, setMapping] = useState<MisMapping | null>(null)
  const [words, setWords] = useState<Record<string, MisStage>>({})
  const [check, setCheck] = useState<MisCheck | null>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState<'apply' | 'remove' | null>(null)

  useEffect(() => {
    Promise.all([mis.file(id), mis.settings()]).then(([v, s]) => {
      setView(v); setSettings(s)
      const l = s.lenders.find((x) => x.slug === v.file.lenderSlug)
      setLender(v.file.lenderSlug ?? ''); setColumns(v.guess); setDateOrder(l?.dateOrder ?? 'dmy'); setKeep(v.keep); setMisDay(v.file.misDay)
      if (v.file.state === 'new') mis.preview(id).then(setPreview, (e: Error) => notify.error('No preview', e.message))
    }, (e: Error) => notify.error('Could not open the file', e.message))
  }, [id])

  const run = async <T,>(what: string, fn: () => Promise<T>) => {
    setBusy(true)
    try { return await fn() } catch (e) { notify.error(what, (e as Error).message); return null } finally { setBusy(false) }
  }
  // A change in an earlier step makes the later steps' results stale.
  const changed = (from: Step) => { if (from <= 2) setMapping(null); setCheck(null) }

  // Step 2's Next: save the mapping; while a status word has no stage it stays on step 2 with the words to fill in.
  const mapAndCheck = async () => {
    const arrived = view ? indiaDay(view.file.arrivedAt) : ''
    const m = await run('Mapping not saved', () => mis.map(id, { lenderSlug: lender, columns, keep, words, dateOrder, misDay: misDay && misDay !== arrived ? misDay : null }))
    if (!m) return
    setMapping(m)
    setWords((w) => ({ ...Object.fromEntries(m.words.filter((x) => x.stage || x.suggested).map((x) => [x.word, (x.stage ?? x.suggested)!])), ...w }))
    if (!m.ready) return
    const c = await run('The check stopped', () => mis.check(id))
    if (c) { setCheck(c); setStep(3) }
  }
  const apply = async () => {
    const c = await run('Not applied', () => mis.apply(id))
    setAsking(null)
    if (c) { setCheck(c); onChanged(); notify.success('MIS applied', `${c.counts.update} updates sent${c.counts.details ? `, ${c.counts.details} details saved` : ''}; the file is deleted from the holding room.`) }
  }
  const remove = async () => {
    const f = await run('Not removed', () => mis.remove(id))
    setAsking(null)
    if (f) { onChanged(); onClose(); notify.success('Removed', `${f.fileName} is deleted from the holding room, unused.`) }
  }

  if (!view || !settings) {
    return <Drawer isOpen onClose={onClose} title="MIS file" defaultMaximized><p className="text-2xs text-muted">Loading…</p></Drawer>
  }
  const f = view.file
  const lenderName = settings.lenders.find((l) => l.slug === lender)?.name ?? ''
  if (f.state !== 'new' && !check?.applied) return <FileDone f={f} lenderName={lenderName} onClose={onClose} />
  const applied = Boolean(check?.applied)
  const arrivedDay = indiaDay(f.arrivedAt)
  const earliest = new Date(Date.parse(`${arrivedDay}T00:00:00Z`) - 365 * 86400000).toISOString().slice(0, 10)
  const backdated = Boolean(misDay) && misDay !== arrivedDay
  const used = new Set(Object.values(columns).filter(Boolean))
  const extra = view.headers.map((h, i) => ({ h, hint: view.hints[i] ?? '' })).filter((x) => !used.has(x.h))
  const total = check ? check.counts.update + check.counts.details : 0
  const next: Record<Step, { label: string; disabled: boolean; go: () => void } | null> = {
    1: { label: 'Next: map the columns', disabled: !lender || !misDay || misDay > arrivedDay || misDay < earliest, go: () => setStep(2) },
    2: { label: mapping && !mapping.ready ? 'Save the status words and match' : 'Next: match the rows', disabled: busy || !columns.status, go: () => void mapAndCheck() },
    3: { label: 'Next: check the changes', disabled: !check, go: () => setStep(4) },
    4: { label: 'Next: apply', disabled: !check, go: () => setStep(5) },
    5: null,
  }
  const n = next[step]

  const footer = applied
    ? <div className="flex flex-wrap items-center gap-2 text-xs"><span>Applied. The file is deleted; its counts stay in the MIS inbox.</span><Link to="/mis/not-matched" className="btn btn-ghost btn-sm">Not matched</Link><Link to="/mis/missing" className="btn btn-ghost btn-sm">Missing from MIS</Link><button type="button" className="btn btn-primary btn-sm ml-auto" onClick={onClose}>Close</button></div>
    : (
      <div className="flex flex-wrap items-center gap-2" data-testid="mis-nav">
        {step > 1 && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setStep((step - 1) as Step)}>‹ Back</button>}
        {n && <button type="button" className="btn btn-primary" disabled={n.disabled} onClick={n.go} data-testid="mis-next">{busy ? 'Working…' : `${n.label} ›`}</button>}
        {step === 5 && check && <button type="button" className="btn btn-primary" disabled={busy || total === 0} onClick={() => setAsking('apply')} data-testid="mis-apply">Apply {check.counts.update} update{check.counts.update === 1 ? '' : 's'}{check.counts.details ? ` + ${check.counts.details} details` : ''}</button>}
        <button type="button" className="btn btn-ghost ml-auto" disabled={busy} onClick={() => setAsking('remove')}>Remove this file</button>
      </div>
    )

  return (
    <Drawer isOpen onClose={onClose} defaultMaximized title={f.fileName} footer={footer}
      subtitle={`${lenderName || 'lender not chosen yet'} · ${f.rowCount} rows · ${f.source === 'mail' ? `mail from ${f.mailFrom}` : `uploaded by ${f.addedBy}`}`}>
      <Stepper step={step} applied={applied} onPick={(s) => !applied && !busy && s < step && setStep(s)} />

      {step === 1 && (
        <div className="space-y-3" data-testid="mis-step-choose">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <section className="card lg:col-span-2">
              <h2 className="section-title">1 · Choose: is this the right MIS?</h2>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <Tile icon={FileSpreadsheet} label="File" value={f.fileName} sub={`${f.kind?.toUpperCase() ?? '—'} · ${f.rowCount} row${f.rowCount === 1 ? '' : 's'}`} />
                <Tile icon={Clock} label="Arrived" value={formatDateTime(f.arrivedAt)} sub={`India day ${arrivedDay}`} />
                <Tile icon={f.source === 'mail' ? Mail : Upload} label={f.source === 'mail' ? 'From' : 'Uploaded by'} value={f.source === 'mail' ? f.mailFrom ?? '—' : f.addedBy} sub={f.mailSubject ?? undefined} />
                <Tile icon={Eraser} label="Cleaned" value={`${f.formulasRemoved} formula${f.formulasRemoved === 1 ? '' : 's'} removed`} sub="text cleaned, nothing else changed" />
              </div>
            </section>
            <section className="card">
              <h2 className="section-title">Lender and MIS date</h2>
              {!f.lenderSlug && (
                <div className="mb-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-2xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  <Info size={15} className="mt-0.5 shrink-0" />
                  <span><b>Lender not known.</b> No lender's mail rule names {f.mailFrom ?? 'this sender'}. Choose it here (and add its sender in MIS settings).</span>
                </div>
              )}
              <label className="block text-2xs font-medium">Lender
                <select className="input mt-1 w-full" aria-label="Lender" value={lender} onChange={(e) => {
                  const l = settings.lenders.find((x) => x.slug === e.target.value)
                  setLender(e.target.value); if (l) { setDateOrder(l.dateOrder); setKeep(l.keepColumns.filter((h) => view.headers.includes(h))) } changed(1)
                }}>
                  <option value="">Choose…</option>
                  {settings.lenders.map((l) => <option key={l.slug} value={l.slug}>{l.name}</option>)}
                </select>
              </label>
              <label className="mt-3 block text-2xs font-medium">MIS date
                <input type="date" className="input mt-1 w-full" aria-label="MIS date" min={earliest} max={arrivedDay} value={misDay} onChange={(e) => { setMisDay(e.target.value); changed(2) }} data-testid="mis-day" />
              </label>
              <p className="mt-1 text-2xs text-muted">The payout cut-off of every change in this file: normally the day it arrived. Set an earlier day only for an old (backdated) MIS, up to a year back.</p>
              {backdated && (
                <p className="mt-2 flex flex-wrap items-center gap-2 text-2xs"><Badge tone="warning"><CalendarClock size={12} className="mr-1 inline" />backdated</Badge> arrived {arrivedDay}
                  <button type="button" className="text-primary underline" onClick={() => { setMisDay(arrivedDay); changed(2) }}>use the day it arrived</button></p>
              )}
            </section>
          </div>
          <section className="card">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-xs font-semibold">Preview{preview ? `: first ${preview.shown} of ${preview.rowCount} rows` : ''}</h3>
              <p className="text-2xs text-muted">Mobiles, PAN and e-mails are partly hidden. Opening a file is written in the audit log.</p>
            </div>
            {!preview ? <p className="text-2xs text-muted">Loading…</p> : (
              <div className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200 dark:border-slate-700" data-testid="mis-preview">
                <table className="table">
                  <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900"><tr><th className="w-10">#</th>{preview.headers.map((h) => <th key={h} className="whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>{preview.rows.map((r, i) => <tr key={i}><td className="text-muted">{i + 1}</td>{r.map((c, j) => <td key={j} className="max-w-[16rem] truncate whitespace-nowrap" title={c}>{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="mis-map">
          <section className="card">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="section-title">2 · Map: which column holds what</h2>
                <p className="text-2xs text-muted">Each list shows the file's columns and what they hold. Saved for {lenderName}: its next MIS fills in by itself.</p>
              </div>
              <label className="text-2xs font-medium">Dates are written
                <select className="input mt-1 w-56" aria-label="Date order" value={dateOrder} onChange={(e) => { setDateOrder(e.target.value as DateOrder); changed(2) }}>
                  <option value="dmy">day first (25/10/2026)</option><option value="mdy">month first (10/25/2026)</option><option value="ymd">year first (2026/10/25)</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
              {FIELDS.map((fl) => (
                <label key={fl.id} className="text-2xs">
                  <span className="font-medium">{fl.label}</span>{fl.help && <span className="text-muted"> · {fl.help}</span>}
                  <select className={`input mt-1 ${columns[fl.id] ? 'border-primary' : ''}`} aria-label={fl.label} value={columns[fl.id] ?? ''} onChange={(e) => { setColumns((c) => ({ ...c, [fl.id]: e.target.value })); changed(2) }}>
                    <option value="">not in this file</option>
                    {view.headers.map((h, i) => <option key={h} value={h}>{h} ({view.hints[i]})</option>)}
                  </select>
                </label>
              ))}
            </div>
          </section>

          <section className="card" data-testid="mis-keep">
            <h3 className="flex items-center gap-1.5 text-xs font-semibold"><NotebookPen size={14} /> Extra columns to keep (metadata)</h3>
            <p className="mb-2 text-2xs text-muted">Tick the other columns worth keeping for each loan (tenure, ROI, product …). Remembered for {lenderName}. Columns with mobiles, PAN or e-mails are never kept.</p>
            {extra.length === 0 ? <p className="text-2xs text-muted">Every column is mapped above.</p> : (
              <div className="flex flex-wrap gap-2">
                {extra.map(({ h, hint }) => {
                  const blocked = NEVER_KEPT.includes(hint)
                  const on = keep.includes(h)
                  return (
                    <label key={h} title={blocked ? `holds ${hint}: never kept` : hint}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-2xs transition ${blocked ? 'cursor-not-allowed border-slate-200 text-muted line-through opacity-60 dark:border-slate-700' : on ? 'cursor-pointer border-primary bg-primary-soft text-primary-text' : 'cursor-pointer border-slate-300 hover:border-primary dark:border-slate-600'}`}>
                      <input type="checkbox" className="sr-only" disabled={blocked} checked={on} onChange={(e) => { setKeep((k) => (e.target.checked ? [...k, h] : k.filter((x) => x !== h))); changed(2) }} />
                      {on && <Check size={12} />}{h}<span className="text-muted">({blocked ? `${hint}: never kept` : hint})</span>
                    </label>
                  )
                })}
              </div>
            )}
          </section>

          {mapping && mapping.words.length > 0 && (
            <section className="card">
              <h3 className="text-xs font-semibold">Status words → our stages</h3>
              <p className="mb-2 text-2xs text-muted">Each word of the lender's status column needs one of our stages. A new word stops the journey until it has one; "cancelled" is a loan cancelled after disbursal (no payout).</p>
              <table className="table"><thead><tr><th>The lender's word</th><th>Rows</th><th>Our stage</th></tr></thead>
                <tbody>{mapping.words.map((w) => (
                  <tr key={w.word}><td>{w.word} {!w.stage && <Badge tone="warning">new</Badge>}</td><td>{w.count}</td>
                    <td><select className="input w-56" aria-label={`Stage of ${w.word}`} value={words[w.word] ?? ''} onChange={(e) => { setWords((x) => ({ ...x, [w.word]: e.target.value as MisStage })); changed(3) }}>
                      <option value="">choose…</option>{STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select></td></tr>
                ))}</tbody>
              </table>
            </section>
          )}
          {mapping && mapping.problems.length > 0 && (
            <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-2xs text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <ul className="list-disc space-y-0.5 pl-4">{mapping.problems.map((p) => <li key={p}>{p}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {step === 3 && check && <MatchView check={check} fileName={f.fileName} />}
      {step === 4 && check && <CheckView check={check} />}
      {step === 5 && check && <ApplyView check={check} lenderName={lenderName} misDay={misDay} backdated={backdated} />}

      <ConfirmDialog isOpen={asking !== null} onClose={() => setAsking(null)} busy={busy} onConfirm={asking === 'apply' ? apply : remove} type={asking === 'apply' ? 'warning' : 'danger'}
        title={asking === 'apply' ? `Apply this MIS of ${lenderName}?` : 'Remove this file unused?'} confirmText={asking === 'apply' ? 'Apply' : 'Remove'}
        message={asking === 'apply' && check ? `MIS date ${misDay}${backdated ? ' (backdated)' : ''}. ${check.counts.update} stage changes go to the lead registry and on to the partner portal (${check.disbursedCount} newly disbursed, ${rupees(check.disbursedTotal)})${check.counts.details ? `; ${check.counts.details} loans get only new details (saved, nothing sent)` : ''}. ${check.missingCount} disbursed loans go to Missing from MIS. The file is then deleted from the holding room.` : 'Its content is deleted from the holding room now.'} />
    </Drawer>
  )
}

/** A file already applied, removed, refused or expired: what happened to it. */
function FileDone({ f, lenderName, onClose }: { f: MisFile; lenderName: string; onClose: () => void }) {
  const s = fileState(f)
  const c = f.counts
  return (
    <Drawer isOpen onClose={onClose} defaultMaximized title={f.fileName} subtitle={lenderName || 'lender not known'}
      footer={<div className="flex justify-end"><button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Close</button></div>}>
      <section className="card mb-3 max-w-3xl" data-testid="mis-file-done">
        <p className="mb-3"><Badge tone={s.tone}>{s.label}</Badge>{s.detail && <span className="ml-2 text-xs text-muted">{s.detail}</span>}</p>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs md:grid-cols-2">
          <Fact k="Arrived" v={formatDateTime(f.arrivedAt)} />
          <Fact k="MIS day" v={f.misDay} />
          <Fact k="From" v={f.source === 'mail' ? `${f.mailFrom ?? '—'}${f.mailSubject ? ` · “${f.mailSubject}”` : ''}` : `uploaded by ${f.addedBy}`} />
          <Fact k="Rows" v={String(f.rowCount)} />
          {f.appliedAt && <Fact k="Applied" v={`${formatDateTime(f.appliedAt)} by ${f.appliedBy ?? '—'}`} />}
        </dl>
        {c && (
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <StatCard icon={RefreshCw} label="Updates" value={c.update ?? 0} /><StatCard icon={Minus} tone="gray" label="Unchanged" value={c.unchanged ?? 0} />
            <StatCard icon={CircleHelp} tone="accent" label="Not matched" value={c.notMatched ?? 0} /><StatCard icon={AlertTriangle} tone="danger" label="Bad rows" value={c.bad ?? 0} />
          </div>
        )}
        {c && (c.notMatched ?? 0) + (c.bad ?? 0) > 0 && <p className="mt-3 text-xs"><Link to="/mis/not-matched">See them in Not matched</Link> (with a CSV for the lender).</p>}
      </section>
    </Drawer>
  )
}

/** The 5 steps as numbered circles on a line: done (tick, can go back to it), current, to come. */
function Stepper({ step, applied, onPick }: { step: Step; applied: boolean; onPick: (s: Step) => void }) {
  return (
    <ol className="mb-4 flex items-start" data-testid="mis-steps">
      {STEPS.map((label, i) => {
        const s = (i + 1) as Step
        const done = applied || s < step
        const now = !applied && s === step
        return (
          <li key={label} className="flex flex-1 items-start last:flex-none" aria-current={now ? 'step' : undefined}>
            <button type="button" className="flex flex-col items-center gap-1 disabled:cursor-default" disabled={!done || applied} onClick={() => onPick(s)}>
              <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-semibold ${done ? 'border-primary bg-primary text-white' : now ? 'border-primary bg-primary-soft text-primary-text' : 'border-slate-300 text-muted dark:border-slate-600'}`}>
                {done ? <Check size={15} /> : s}
              </span>
              <span className={`text-2xs ${now ? 'font-semibold' : done ? '' : 'text-muted'}`}>{label}</span>
            </button>
            {s < 5 && <span className={`mx-2 mt-4 h-0.5 flex-1 ${done ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`} />}
          </li>
        )
      })}
    </ol>
  )
}

function Tile({ icon: Icon, label, value, sub }: { icon: LucideIcon; label: string; value: string; sub?: string }) {
  return (
    <div className="flex min-w-0 gap-2.5 rounded-lg border border-slate-200 p-2.5 dark:border-slate-700">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary-text"><Icon size={15} /></span>
      <div className="min-w-0"><p className="text-2xs text-muted">{label}</p><p className="truncate text-xs font-semibold" title={value}>{value}</p>{sub && <p className="truncate text-2xs text-muted" title={sub}>{sub}</p>}</div>
    </div>
  )
}

function Fact({ k, v }: { k: string; v: string }) {
  return <div className="flex gap-2"><dt className="w-20 shrink-0 text-muted">{k}</dt><dd>{v}</dd></div>
}

function NotesTable({ notes }: { notes: MisRowNote[] }) {
  return (
    <table className="table"><thead><tr><th>Row</th><th>Kind</th><th>Lead code</th><th>Lender ref</th><th>Status</th><th>Reason</th></tr></thead>
      <tbody>{notes.map((n) => <tr key={`${n.row}-${n.kind}`}><td>{n.row}</td><td><Badge tone={n.kind === 'bad' ? 'danger' : 'warning'}>{n.kind === 'bad' ? 'bad row' : 'not matched'}</Badge></td><td className="mono">{n.leadCode ?? '—'}</td><td className="mono">{n.lenderRef ?? '—'}</td><td>{n.status ?? '—'}</td><td className="text-2xs">{n.reason}</td></tr>)}</tbody>
    </table>
  )
}

/** Step 3: how each row was found (lead code → our application ID / lender ref → mobile), and the rows that were not. */
function MatchView({ check, fileName }: { check: MisCheck; fileName: string }) {
  const c = check.counts
  const matched = c.rows - c.notMatched - c.bad
  const notMatched = check.notes.filter((x) => x.kind === 'not_matched')
  return (
    <section className="card mb-3" data-testid="mis-match">
      <h2 className="section-title">3 · Match: whose loan is each row</h2>
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <StatCard icon={Rows3} tone="gray" label="Rows" value={c.rows} /><StatCard icon={Link2} label="Matched" value={matched} />
        <StatCard icon={CircleHelp} tone="accent" label="Not matched" value={c.notMatched} /><StatCard icon={AlertTriangle} tone="danger" label="Bad rows" value={c.bad} />
      </div>
      <table className="table mb-3 max-w-md"><thead><tr><th>Matched by</th><th>Rows</th></tr></thead>
        <tbody>
          {(Object.keys(MATCHED_BY) as (keyof typeof MATCHED_BY)[]).map((k) => <tr key={k}><td>{MATCHED_BY[k]}</td><td>{check.matchedBy[k]}</td></tr>)}
          <tr><td className="text-2xs text-muted">of them found in MIS (our lead code, no send of ours to this lender)</td><td>{check.foundInMis}</td></tr>
        </tbody>
      </table>
      {check.problems.map((p) => <p key={p} className="field-error">{p}</p>)}
      {notMatched.length > 0 && (
        <>
          <div className="mb-1 flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold">Not matched (first {notMatched.length})</h3>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => download(`${fileName}-not-matched.csv`, notesCsv(notMatched.map((r) => ({ ...r, fileName }))))}>CSV</button>
          </div>
          <NotesTable notes={notMatched} />
        </>
      )}
    </section>
  )
}

/** Step 4: what the file changes (nothing saved yet). */
function CheckView({ check }: { check: MisCheck }) {
  const c = check.counts
  const bad = check.notes.filter((x) => x.kind === 'bad')
  return (
    <section className="card mb-3" data-testid="mis-check">
      <h2 className="section-title">4 · Check: what changes (nothing saved yet)</h2>
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <StatCard icon={RefreshCw} label="Updates" value={c.update} /><StatCard icon={Minus} tone="gray" label="Unchanged" value={c.unchanged} />
        <StatCard icon={IndianRupee} label={`Newly disbursed (${check.disbursedCount})`} value={rupees(check.disbursedTotal)} /><StatCard icon={AlertTriangle} tone="danger" label="Missing from MIS" value={check.missingCount} />
      </div>
      {c.details > 0 && <p className="mb-2 text-xs"><Badge tone="gray">details only: {c.details}</Badge> loans whose remark, sanction date or extra columns changed: saved, nothing sent to the partner portal.</p>}
      {check.changes.length > 0 && <p className="mb-2 text-xs">{check.changes.map((ch) => `${ch.count} ${stageLabel(ch.from)} → ${stageLabel(ch.to)}`).join(' · ')}</p>}
      {check.missingCount > 0 && <p className="mb-2 text-xs"><Badge tone="warning">Missing from MIS: {check.missingCount}</Badge> disbursed loans of the last 30 days that this file no longer has: {check.missing.map((m) => m.personCode ?? m.lineKey).slice(0, 10).join(', ')}</p>}
      {check.updates.length > 0 && (
        <details open><summary className="cursor-pointer text-xs font-semibold">Updates (first {check.updates.length})</summary>
          <table className="table"><thead><tr><th>Row</th><th>Lead code</th><th>Stage</th><th>Stage day</th><th>Sanctioned</th><th>Disbursed</th><th>Remark</th><th>Matched by</th></tr></thead>
            <tbody>{check.updates.map((u) => <tr key={u.row}><td>{u.row}</td><td className="mono">{u.personCode ?? '—'}</td><td>{u.detailsOnly ? <Badge tone="gray">details only</Badge> : <>{stageLabel(u.from)} → {stageLabel(u.to)}</>}</td><td className="mono">{u.statusOn}</td><td className="mono">{u.sanctionedOn ?? '—'}</td><td>{rupees(u.disbursedAmount)}</td><td className="max-w-[14rem] truncate text-2xs" title={u.remark ?? ''}>{u.remark ?? '—'}</td><td className="text-2xs">{MATCHED_BY[u.matchedBy]}{u.foundInMis && u.matchedBy === 'lead_code' ? ' · found in MIS' : ''}</td></tr>)}</tbody>
          </table>
        </details>
      )}
      {bad.length > 0 && <details><summary className="cursor-pointer text-xs font-semibold">Bad rows (first {bad.length})</summary><NotesTable notes={bad} /></details>}
    </section>
  )
}

/** Step 5: the summary before Apply, then the result. */
function ApplyView({ check, lenderName, misDay, backdated }: { check: MisCheck; lenderName: string; misDay: string; backdated: boolean }) {
  const c = check.counts
  return (
    <section className="card mb-3" data-testid="mis-apply-step">
      <h2 className="section-title">{check.applied ? '5 · Applied' : '5 · Apply'} · {lenderName}</h2>
      <ul className="list-disc space-y-1 pl-5 text-xs">
        <li>MIS date (the payout cut-off): <b>{misDay}</b>{backdated && <> <Badge tone="warning">backdated</Badge></>}.</li>
        <li><b>{c.update}</b> stage changes {check.applied ? 'went' : 'go'} to the lead registry and on to the partner portal{check.changes.length ? ` (${check.changes.map((ch) => `${ch.count} ${stageLabel(ch.from)} → ${stageLabel(ch.to)}`).join(', ')})` : ''}.</li>
        <li><b>{check.disbursedCount}</b> newly disbursed, {rupees(check.disbursedTotal)}.</li>
        {c.details > 0 && <li><b>{c.details}</b> loans {check.applied ? 'got' : 'get'} only new details (remark, sanction date, extra columns): saved, nothing sent.</li>}
        <li><b>{check.missingCount}</b> disbursed loans {check.applied ? 'went' : 'go'} to Missing from MIS.</li>
        <li><b>{c.notMatched}</b> not matched and <b>{c.bad}</b> bad rows {check.applied ? 'are' : 'go'} in Not matched, with a CSV for the lender.</li>
        <li>{check.applied ? 'The file is deleted from the holding room.' : 'The file is then deleted from the holding room.'}</li>
      </ul>
      {c.update + c.details === 0 && !check.applied && <p className="mt-2 text-xs text-muted">Nothing to apply: no row changes anything. Remove the file, or go back and check the mapping.</p>}
    </section>
  )
}
